import { getAppSetupTheme, getAppThemeDraft } from "@/api";
import { ServerThemeType } from "@brandclub/common-ui";

type ThemeDraftType = "draft" | "setup";
export class ThemeDraft {
  /**
   * The identifier for the theme draft in the URL and session storage
   */
  private static readonly THEME_DRAFT_IDENTIFIER = "themedraftid";
  /**
   * The identifier for the theme setup in the URL and session storage
   */
  private static readonly THEME_SETUP_IDENTIFIER = "themesetupid";

  private static resolveIdentifier(type: "draft" | "setup"): string {
    if (type !== "draft" && type !== "setup") {
      throw new Error("Invalid type");
    }
    return type === "draft"
      ? this.THEME_DRAFT_IDENTIFIER
      : this.THEME_SETUP_IDENTIFIER;
  }

  private static getIdFromUrl(type: ThemeDraftType): string | null {
    const identifier = this.resolveIdentifier(type);
    const params = new URLSearchParams(window.location.search);
    const id = params.get(identifier);
    if (!id || id === "undefined" || id === "null") {
      return null;
    }
    return id;
  }

  private static getIdFromStorage(type: ThemeDraftType): string | null {
    const identifier = this.resolveIdentifier(type);
    const id = sessionStorage.getItem(identifier);
    if (!id || id === "undefined" || id === "null") {
      return null;
    }
    return id;
  }

  static getCurrentId(type: ThemeDraftType): string | null {
    const fixedPreviewId = (window as any).bcPreviewThemeId;

    if (fixedPreviewId) {
      this.saveId(fixedPreviewId, type);
      return fixedPreviewId;
    }

    const urlId = this.getIdFromUrl(type);
    if (urlId) {
      this.saveId(urlId, type);
      return urlId;
    }
    return this.getIdFromStorage(type);
  }

  static saveId(id: string, type: ThemeDraftType): void {
    sessionStorage.setItem(this.resolveIdentifier(type), id);
  }

  static clearId(type: ThemeDraftType): void {
    sessionStorage.removeItem(this.resolveIdentifier(type));
  }

  static async getTheme(appId: string): Promise<ServerThemeType | null> {
    const appThemeId = this.getCurrentId("draft");
    if (!appThemeId) {
      return null;
    }

    try {
      const response = await getAppThemeDraft(appId, appThemeId);
      if (response?.status === "success") {
        this.saveId(response.draftId, "draft");
        return { ...response.brandAppTheme, draftId: response.draftId };
      }
      if (response.status === "notFound") {
        this.clearId("draft");
        return null;
      }
      throw new Error(response.errorMessage);
    } catch (error) {
      console.error("Error fetching theme draft:", error);
      return null;
    }
  }

  static async getSetupTheme(appId: string) {
    const setupId = this.getCurrentId("setup");
    if (!setupId) {
      return null;
    }

    try {
      const response = await getAppSetupTheme(appId, setupId);
      if (response?.status === "success") {
        const { status: _status, ...rest } = response;
        return { ...rest, setupId };
      }
      if (response.status === "notFound") {
        this.clearId("setup");
        return null;
      }
      throw new Error(response.errorMessage);
    } catch (error) {
      console.error("Error fetching theme draft:", error);
      return null;
    }
  }
}
